import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Grid,
  Container,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { getTemplate } from "../services/api";
import { useNotification } from "../../interviewAI/NotificationContext";
import CodeIcon from "@mui/icons-material/Code";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Footer from "../../interviewAI/layout/Footer";
import logo from "../../interviewAI/assets/TalentTalkslogo.png";
import { format } from "date-fns";

const PublicTemplatePreview = () => {
  const { templateId } = useParams();
  const { notify } = useNotification();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const data = await getTemplate(templateId);
        setTemplate(data);
      } catch (err) {
        const message = err?.message || "Failed to load job opening";
        console.error("Error fetching template:", err);
        setError(message);
        notify("error", message);
      } finally {
        setLoading(false);
      }
    };

    if (templateId) {
      fetchTemplate();
    }
  }, [templateId]);

  if (loading) {
    return (
      <Box
        sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      >
        <Header />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f5f5f5",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={60} />
            <Typography
              variant="h6"
              sx={{ mt: 3, color: "text.secondary", fontWeight: 600 }}
            >
              Loading job opening...
            </Typography>
          </Box>
        </Box>
        <Footer />
      </Box>
    );
  }

  if (error || !template) {
    return (
      <Box
        sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      >
        <Header />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f5f5f5",
            px: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              maxWidth: 500,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 1, color: "error.main" }}
            >
              Job Opening Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {error || "The job opening you're looking for doesn't exist."}
            </Typography>
          </Paper>
        </Box>
        <Footer />
      </Box>
    );
  }

  const statusColor = {
    ACTIVE: { bg: "#e8f5e9", text: "#2e7d32" },
    INACTIVE: { bg: "#f5f5f5", text: "#616161" },
    HOLD: { bg: "#fff3e0", text: "#e65100" },
    CLOSED: { bg: "#ffebee", text: "#c62828" },
  };

  const currentStatus = template.templateStatus || "ACTIVE";
  const colors = statusColor[currentStatus] || statusColor.ACTIVE;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f9f9f9",
      }}
    >
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
          py: { xs: 3, md: 4 },
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: { xs: 200, md: 350 },
            height: { xs: 200, md: 350 },
            background: "rgba(255, 255, 255, 0.08)",
            borderRadius: "50%",
            transform: "translate(30%, -30%)",
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ mb: 2 }}>
            <Chip
              icon={
                currentStatus === "ACTIVE" ? <CheckCircleIcon /> : undefined
              }
              label={currentStatus}
              sx={{
                fontWeight: 700,
                fontSize: "0.8rem",
                px: 2,
                py: 2,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                textTransform: "uppercase",
                backdropFilter: "blur(10px)",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              mb: 1.5,
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.4rem" },
              lineHeight: 1.3,
            }}
          >
            {template.name}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              opacity: 0.9,
              fontSize: { xs: "0.95rem", md: "1rem" },
              maxWidth: "600px",
            }}
          >
            Join our talented team and grow your career with us
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, background: "#f9f9f9", py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Left Column - Job Description */}
            <Grid item xs={12} md={7}>
              {template.jd && (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#555",
                      fontSize: "0.95rem",
                      lineHeight: 1.8,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {template.jd}
                  </Typography>
                </Paper>
              )}
            </Grid>

            {/* Right Column - Stats & Tech Stack */}
            <Grid item xs={12} md={5}>
              {/* Stats Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  mb: 3,
                }}
              >
                <Stack spacing={2.5}>
                  {template.interviewDuration && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          color: "#999",
                          mb: 0.5,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Duration
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.5rem",
                          fontWeight: 800,
                          color: "#0F766E",
                        }}
                      >
                        {template.interviewDuration}m
                      </Typography>
                    </Box>
                  )}
                  {template.createdAt && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          color: "#999",
                          mb: 0.5,
                          letterSpacing: "0.5px",
                        }}
                      >
                        Posted
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color: "#1a1a1a",
                        }}
                      >
                        {format(new Date(template.createdAt), "MMM d, yyyy")}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>

              {/* Tech Stack Card */}
              {(template.techStackData?.length > 0 ||
                template.codingLanguage) && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Stack spacing={3}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: 800,
                          color: "#1a1a1a",
                          mb: 2,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Tech Stack
                      </Typography>
                      {template.techStackData &&
                        template.techStackData.length > 0 && (
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ flexWrap: "wrap", gap: 1 }}
                          >
                            {template.techStackData.map((tech, idx) => (
                              <Chip
                                key={idx}
                                label={tech.name}
                                sx={{
                                  backgroundColor: "#e8f5f1",
                                  color: "#0F766E",
                                  fontWeight: 600,
                                  fontSize: "0.8rem",
                                  px: 1.5,
                                  py: 1.8,
                                  borderRadius: 1,
                                  border: "1px solid #c0e5df",
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                    </Box>

                    {template.codingLanguage && (
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "#999",
                            mb: 0.5,
                            letterSpacing: "0.5px",
                          }}
                        >
                          Coding Language
                        </Typography>
                        <Chip
                          label={template.codingLanguage}
                          sx={{
                            backgroundColor: "#fff3e0",
                            color: "#e65100",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            px: 1.5,
                            py: 1.8,
                            borderRadius: 1,
                            border: "1px solid #ffe0b2",
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                </Paper>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

const Header = () => {
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: "2rem",
        paddingRight: "2rem",
        paddingTop: "1.25rem",
        paddingBottom: "1.25rem",
        borderBottom: "4px solid rgb(20, 184, 166)",
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.95), rgba(255,255,255,0.95))",
        backdropFilter: "blur(12px)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <img
          src={logo}
          alt="TalentTalks Logo"
          style={{
            height: "2.2rem", // 35px
            width: "auto",
            objectFit: "contain",
            cursor: "pointer",
            transition: "transform 0.3s ease",
            display: "block",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = "scale(1.03)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        />

        <span
          style={{
            fontSize: "0.8rem",
            color: "rgb(13, 148, 136)",
            fontWeight: 600,
            letterSpacing: "0.3px",
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          AI Interview Platform
        </span>
      </div>
    </header>
  );
};

export default PublicTemplatePreview;
